import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  getReviewForm,
  getReviewFormSettings,
  getQuestionScreenSettings,
  stringToColor,
  getCurrentUser,
  getReviewFormPages,
  getAllReviewQuestions,
  submitAnswersWithLottery,
  saveReviewFormAnswers,
  supabase
} from '../lib/supabase';

// Import required questions data(初回来店者向け/2回目以降の来店者向けで別配列)
import {
  FIRST_TIME_PAGES,
  FIRST_TIME_QUESTIONS,
  REPEAT_PAGES,
  REPEAT_QUESTIONS,
  getRequiredQuestionsForPage,
  getRequiredPageByNumber
} from '../config/questions/requiredQuestions';

// Import question type components
import ShortTextQuestion from './QuestionTypes/ShortTextQuestion';
import LongTextQuestion from './QuestionTypes/LongTextQuestion';
import SingleChoiceQuestion from './QuestionTypes/SingleChoiceQuestion';
import PullDownQuestion from './QuestionTypes/PullDownQuestion';
import PreviewIndicator from './PreviewIndicator';
import DrawingLoadingScreen from './DrawingLoadingScreen';

const RequiredQuestionsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State management
  const [loading, setLoading] = useState(true);
  const [reviewForm, setReviewForm] = useState(null);
  const [formSettings, setFormSettings] = useState(null);
  const [questionSettings, setQuestionSettings] = useState(null);
  const [allPages, setAllPages] = useState([]);
  const [currentPageData, setCurrentPageData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [allQuestions, setAllQuestions] = useState([]);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasRegularQuestions, setHasRegularQuestions] = useState(false);
  const [showDrawingLoading, setShowDrawingLoading] = useState(false);

  const reviewFormId = searchParams.get('reviewFormId');
  const currentPage = parseInt(searchParams.get('page') || '1');
  const isPreviewMode = searchParams.get('mode') === 'preview';
  const storeCode = searchParams.get('storeCode');
  // visitorType: WelcomePageがcheckAnswerEligibilityの結果を渡す('first'|'repeat')。
  // 未指定(プレビュー等)の場合は初回向けを既定とする。
  const visitorType = searchParams.get('visitorType') === 'repeat' ? 'repeat' : 'first';

  useEffect(() => {
    loadRequiredQuestionsData();
    loadCurrentUser();
  }, [reviewFormId, currentPage, visitorType]);

  // Handle OAuth redirect flow
  useEffect(() => {
    const handleOAuthRedirect = async () => {

      // Check if this is an OAuth redirect (no reviewFormId in URL but has pending one in localStorage)
      if (!reviewFormId) {
        const pendingReviewFormId = localStorage.getItem('pendingReviewFormId');
        const pendingStoreCode = localStorage.getItem('pendingStoreCode');

        if (pendingReviewFormId) {
          // DON'T remove from localStorage yet - keep it as backup
          const storeParam = pendingStoreCode ? `&storeCode=${pendingStoreCode}` : '';
          navigate(`/required-questions?reviewFormId=${pendingReviewFormId}&page=1${storeParam}`, { replace: true });
          return; // Exit early to prevent loading with null reviewFormId
        } else {
          // Small delay to allow localStorage to be ready
          setTimeout(() => {
            const delayedCheck = localStorage.getItem('pendingReviewFormId');
            const delayedStoreCode = localStorage.getItem('pendingStoreCode');
            if (delayedCheck) {
              const storeParam = delayedStoreCode ? `&storeCode=${delayedStoreCode}` : '';
              navigate(`/required-questions?reviewFormId=${delayedCheck}&page=1${storeParam}`, { replace: true });
            } else {
              navigate('/not-found');
            }
          }, 500);
          return;
        }
      } else {
        // If we have reviewFormId, clean up localStorage
        const pendingReviewFormId = localStorage.getItem('pendingReviewFormId');
        const pendingStoreCode = localStorage.getItem('pendingStoreCode');
        if (pendingReviewFormId) {
          localStorage.removeItem('pendingReviewFormId');
        }
        if (pendingStoreCode) {
          localStorage.removeItem('pendingStoreCode');
        }
      }
    };

    handleOAuthRedirect();
  }, [reviewFormId, navigate]);

  // Check page access rights after data is loaded
  useEffect(() => {
    if (!loading && allPages.length > 0 && allQuestions.length > 0) {
      if (!checkPageAccess()) {
        // User is trying to access a page they shouldn't
        const firstIncompletePage = findFirstIncompletePageNumber();
        
        console.log('Page access denied - currentPage:', currentPage, 'firstIncompletePage:', firstIncompletePage);
        
        // If all pages are completed but user is trying to access a page beyond the last page
        const redirectPage = firstIncompletePage > allPages.length ? allPages.length : firstIncompletePage;
        
        
        // Redirect to the first incomplete page
        const storeParam = storeCode ? `&storeCode=${storeCode}` : '';
        const modeParam = isPreviewMode ? '&mode=preview' : '';
        navigate(`/required-questions?reviewFormId=${reviewFormId}&page=${redirectPage}&visitorType=${visitorType}${storeParam}${modeParam}`, { replace: true });
      }
    }
  }, [loading, allPages, allQuestions, answers, currentPage, reviewFormId, navigate]);

  const loadCurrentUser = async () => {
    // プレビューモードではダミーユーザーを設定
    if (isPreviewMode) {
      setCurrentUser({ id: 'preview-user', email: 'preview@example.com' });
      return;
    }

    try {
      const user = await getCurrentUser();
      if (!user) {
        // User not logged in, redirect to home
        navigate(`/?reviewFormId=${reviewFormId}`);
        return;
      }
      setCurrentUser(user);
    } catch (error) {
      console.error('Error loading current user:', error);
      navigate(`/?reviewFormId=${reviewFormId}`);
    }
  };

  const loadRequiredQuestionsData = async () => {
    console.log('loadRequiredQuestionsData called - currentPage:', currentPage);
    
    if (!reviewFormId) {
      // Don't navigate here - let the OAuth redirect handler manage this
      return;
    }

    setLoading(true);
    
    try {
      // Load form basic data and settings
      const [formData, settingsData, questionSettingsData, regularPages, allRegularQuestions] = await Promise.all([
        getReviewForm(reviewFormId, isPreviewMode),
        getReviewFormSettings(reviewFormId),
        getQuestionScreenSettings(reviewFormId),
        getReviewFormPages(reviewFormId, isPreviewMode),
        getAllReviewQuestions(reviewFormId)
      ]);

      if (!formData) {
        navigate('/not-found');
        return;
      }

      setReviewForm(formData);
      setFormSettings(settingsData);
      setQuestionSettings(questionSettingsData);

      // Check if there are regular questions (pages exist AND questions exist)
      setHasRegularQuestions(regularPages && regularPages.length > 0 && allRegularQuestions && allRegularQuestions.length > 0);

      // visitorType('first'|'repeat')に応じたページ・質問セットを使用
      const pages = visitorType === 'repeat' ? REPEAT_PAGES : FIRST_TIME_PAGES;
      const allQuestionsForType = visitorType === 'repeat' ? REPEAT_QUESTIONS : FIRST_TIME_QUESTIONS;
      setAllPages(pages);
      setAllQuestions(allQuestionsForType);

      // Find current page
      const pageData = getRequiredPageByNumber(currentPage, visitorType);
      if (!pageData) {
        navigate('/not-found');
        return;
      }

      setCurrentPageData(pageData);

      // Load questions for current page
      const questionsData = getRequiredQuestionsForPage(currentPage, visitorType);
      setQuestions(questionsData);

      // Load stored answers from localStorage (persist across pages)
      const storedAnswers = localStorage.getItem('requiredQuestionAnswers');
      if (storedAnswers) {
        setAnswers(JSON.parse(storedAnswers));
      }

    } catch (error) {
      console.error('Error loading required questions data:', error);
      navigate('/not-found');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answerData) => {
    const newAnswers = {
      ...answers,
      [questionId]: answerData
    };
    setAnswers(newAnswers);
    
    // Persist answers to localStorage
    localStorage.setItem('requiredQuestionAnswers', JSON.stringify(newAnswers));
    
    // Update progress information (only if data is loaded)
    if (allPages.length > 0 && allQuestions.length > 0) {
      const lastCompletedPage = findLastCompletedPageNumber();
      const firstIncompletePage = findFirstIncompletePageNumber();
      const maxAccessiblePage = firstIncompletePage;
      localStorage.setItem(`progress_${reviewFormId}`, JSON.stringify({
        lastCompletedPage,
        firstIncompletePage,
        maxAccessiblePage,
        lastUpdated: new Date().toISOString()
      }));
    }
  };

  // question.requiredUnless: { questionId, value } が設定されている場合、
  // その質問の回答が value と一致する間は is_required を無効化する
  // (例: 「気になった点」で「特になし」を選んだ場合、そのコメント欄は必須にしない)
  const isEffectivelyRequired = (question, currentAnswers) => {
    if (!question.is_required) return false;
    if (question.requiredUnless) {
      const dependentAnswer = currentAnswers[question.requiredUnless.questionId];
      if (dependentAnswer?.answer === question.requiredUnless.value) {
        return false;
      }
    }
    return true;
  };

  const isAnswerFilled = (question, answer) => {
    if (!answer) return false;
    switch (question.question_type_id) {
      case 1: // Short text
      case 2: // Long text
        return !!(answer.answer && answer.answer.trim() !== '');
      case 3: // Single choice
      case 8: // Pull down
        return answer.answer !== undefined && answer.answer !== '' && answer.answer !== null;
      default:
        return !!(answer.answer && answer.answer !== '');
    }
  };

  const validateCurrentPageRequiredQuestions = () => {
    const errors = {};
    let hasErrors = false;

    questions.forEach(question => {
      if (isEffectivelyRequired(question, answers) && !isAnswerFilled(question, answers[question.id])) {
        errors[question.id] = '必須項目です。回答してください。';
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  const validateAllRequiredQuestions = () => {
    const errors = {};
    let hasErrors = false;
    let firstErrorQuestionId = null;

    allQuestions.forEach(question => {
      if (isEffectivelyRequired(question, answers) && !isAnswerFilled(question, answers[question.id])) {
        errors[question.id] = '必須項目です。回答してください。';
        hasErrors = true;
        if (!firstErrorQuestionId) {
          firstErrorQuestionId = question.id;
        }
      }
    });

    setValidationErrors(errors);
    return { isValid: !hasErrors, firstErrorQuestionId };
  };

  const scrollToQuestion = (questionId) => {
    const questionElement = document.getElementById(`question-${questionId}`);
    if (questionElement) {
      questionElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      // Add highlight effect
      questionElement.style.animation = 'highlightError 2s ease-out';
      setTimeout(() => {
        if (questionElement) {
          questionElement.style.animation = '';
        }
      }, 2000);
    }
  };

  // Check if a question is properly answered
  // Find the first page with unanswered required questions
  const findFirstIncompletePageNumber = () => {
    for (let i = 0; i < allPages.length; i++) {
      const pageNumber = i + 1;
      const pageQuestions = allQuestions.filter(q => q.pege_number === pageNumber);

      const hasIncompleteRequired = pageQuestions.some(question => {
        if (!isEffectivelyRequired(question, answers)) return false;
        return !isAnswerFilled(question, answers[question.id]);
      });

      if (hasIncompleteRequired) {
        return pageNumber;
      }
    }

    // All pages completed
    return allPages.length + 1; // Beyond last page means all completed
  };

  // Find the last completed page number (all required questions answered)
  const findLastCompletedPageNumber = () => {
    let lastCompletedPage = 0;

    for (let i = 0; i < allPages.length; i++) {
      const pageNumber = i + 1;
      const pageQuestions = allQuestions.filter(q => q.pege_number === pageNumber);

      const allRequiredAnswered = pageQuestions.every(question => {
        if (!isEffectivelyRequired(question, answers)) return true;
        return isAnswerFilled(question, answers[question.id]);
      });

      if (allRequiredAnswered) {
        lastCompletedPage = pageNumber;
      } else {
        break;
      }
    }

    return lastCompletedPage;
  };

  // Check if current page access is valid
  const checkPageAccess = () => {
    if (!allPages.length || !allQuestions.length) return true; // Still loading

    // Always allow page 1
    if (currentPage === 1) return true;

    // For other pages, check if there are any incomplete pages before this one
    const firstIncompletePage = findFirstIncompletePageNumber();

    // If there's an incomplete page before the current page, access is denied
    return firstIncompletePage >= currentPage;
  };

  const handleNextClick = () => {
    // Clear previous validation errors
    setValidationErrors({});

    if (!validateCurrentPageRequiredQuestions()) {
      const firstErrorQuestion = questions.find(q => validationErrors[q.id]);
      if (firstErrorQuestion) {
        setTimeout(() => scrollToQuestion(firstErrorQuestion.id), 100);
      }
      return;
    }

    const totalPages = allPages.length;

    if (currentPage >= totalPages) {
      // Final page - validate all questions before showing submit dialog
      const { isValid, firstErrorQuestionId } = validateAllRequiredQuestions();

      if (!isValid) {
        // Find the page containing the first error question
        const errorQuestion = allQuestions.find(q => q.id === firstErrorQuestionId);

        if (errorQuestion) {
          const errorPageNumber = errorQuestion.pege_number;

          if (errorPageNumber) {
            if (errorPageNumber !== currentPage) {
              // Navigate to the page with the error
              const storeParam = storeCode ? `&storeCode=${storeCode}` : '';
              const modeParam = isPreviewMode ? '&mode=preview' : '';
              navigate(`/required-questions?reviewFormId=${reviewFormId}&page=${errorPageNumber}&visitorType=${visitorType}${storeParam}${modeParam}`);
              // Set a timeout to scroll after navigation
              setTimeout(() => scrollToQuestion(firstErrorQuestionId), 500);
            } else {
              // Error is on current page, just scroll
              scrollToQuestion(firstErrorQuestionId);
            }
          }
        }
        return;
      }

      // All required questions answered
      if (hasRegularQuestions) {
        // If there are regular questions, go directly to them
        const storeParam = storeCode ? `&storeCode=${storeCode}` : '';
        const modeParam = isPreviewMode ? '&mode=preview' : '';
        navigate(`/questions?reviewFormId=${reviewFormId}&page=1${storeParam}${modeParam}`);
      } else {
        // If only preset questions, show submit confirmation
        setShowSubmitDialog(true);
      }
    } else {
      // Go to next page
      const nextPage = currentPage + 1;
      const storeParam = storeCode ? `&storeCode=${storeCode}` : '';
      const modeParam = isPreviewMode ? '&mode=preview' : '';
      navigate(`/required-questions?reviewFormId=${reviewFormId}&page=${nextPage}&visitorType=${visitorType}${storeParam}${modeParam}`);
    }
  };

  const handleSubmitConfirm = async () => {
    setSubmitting(true);
    setShowSubmitDialog(false);

    if (hasRegularQuestions) {
      // Navigate to regular questions page
      const storeParam = storeCode ? `&storeCode=${storeCode}` : '';
      const modeParam = isPreviewMode ? '&mode=preview' : '';
      navigate(`/questions?reviewFormId=${reviewFormId}&page=1${storeParam}${modeParam}`);
    } else {
      // Only preset questions - submit answers directly
      try {
        // Skip submission in preview mode
        if (isPreviewMode) {
          navigate(`/completion?reviewFormId=${reviewFormId}&mode=preview`);
          return;
        }

        // Check if user is logged in
        if (!currentUser) {
          console.error('No user logged in');
          navigate(`/?reviewFormId=${reviewFormId}`);
          return;
        }

        // Prepare answers data
        const answersData = { ...answers };

        // Submit using the lottery system (which handles regular submissions too)
        const result = await submitAnswersWithLottery(
          reviewFormId,
          currentUser.id,
          answersData,
          storeCode || null
        );

        if (!result.success || result.error) {
          console.error('Submission error:', result.error || 'Unknown error');
          alert('回答の送信中にエラーが発生しました。もう一度お試しください。');
          setSubmitting(false);
          return;
        }

        // Note: All data saving is handled by the Edge Function
        // No need for additional saves here

        // Clear local storage
        localStorage.removeItem('requiredQuestionAnswers');
        localStorage.removeItem(`progress_${reviewFormId}`);

        // Handle different lottery results
        if (result.cooldownActive) {
          // User answered within 5 days - redirect to cooldown page
          navigate(`/cooldown?reviewFormId=${reviewFormId}`);
          return;
        }
        
        // Store submission data for later use
        window.submissionData = { 
          reviewFormId, 
          submissionId: result.submissionId,
          isWinner: result.isWinner,
          message: result.message,
          winnerId: result.winnerToken || result.winnerId
        };
        
        // Show drawing loading screen
        setShowSubmitDialog(false);
        setShowDrawingLoading(true);
      } catch (error) {
        console.error('Error submitting answers:', error);
        alert('回答の送信中にエラーが発生しました。もう一度お試しください。');
        setSubmitting(false);
      }
    }
  };

  // Handle drawing loading completion
  const handleDrawingComplete = () => {
    setShowDrawingLoading(false);
    
    if (isPreviewMode) {
      navigate(`/completion?reviewFormId=${reviewFormId}&mode=preview`);
    } else {
      const submissionData = window.submissionData;
      if (submissionData) {
        if (submissionData.isWinner) {
          // Navigate to winner page with token
          navigate(`/winner?reviewFormId=${submissionData.reviewFormId}&submissionId=${submissionData.submissionId}&token=${submissionData.winnerId}`);
        } else {
          // Navigate to regular completion page
          navigate(`/completion?reviewFormId=${submissionData.reviewFormId}&submissionId=${submissionData.submissionId}`);
        }
        delete window.submissionData; // Clean up
      }
    }
  };

  const renderQuestion = (question, index) => {
    const questionNumber = index + 1;
    const totalQuestions = questions.length;
    const themeColor = formSettings?.theme_color || '#8C52FF';
    const hasValidationError = validationErrors[question.id];

    // console.log('Rendering question:', { id: question.id, type: question.question_type_id });

    // Add existing answer to question object
    const questionWithAnswer = {
      ...question,
      existingAnswer: answers[question.id]?.answer,
      existingAnswers: answers[question.id]?.answers,
      existingOtherText: answers[question.id]?.other_text,
      hasValidationError,
      validationMessage: validationErrors[question.id]
    };
    
    // // Log question type 11 specifically
    // if (question.question_type_id === 11) {
    //   console.log('Question Type 11 data:', {
    //     id: question.id,
    //     is_tournament: question.is_tournament,
    //     has_tournament_attributes: !!question.tournament_attributes,
    //     tournament_attributes_length: question.tournament_attributes?.length
    //   });
    // }

    switch (question.question_type_id) {
      case 1:
        return (
          <Box key={question.id} id={`question-${question.id}`}>
            <ShortTextQuestion
              question={questionWithAnswer}
              themeColor={themeColor}
              currentQuestion={questionNumber}
              totalQuestions={totalQuestions}
              onAnswerChange={handleAnswerChange}
            />
          </Box>
        );
      case 2:
        return (
          <Box key={question.id} id={`question-${question.id}`}>
            <LongTextQuestion
              question={questionWithAnswer}
              themeColor={themeColor}
              currentQuestion={questionNumber}
              totalQuestions={totalQuestions}
              onAnswerChange={handleAnswerChange}
            />
          </Box>
        );
      case 3:
        return (
          <Box key={question.id} id={`question-${question.id}`}>
            <SingleChoiceQuestion
              question={questionWithAnswer}
              themeColor={themeColor}
              currentQuestion={questionNumber}
              totalQuestions={totalQuestions}
              onAnswerChange={handleAnswerChange}
            />
          </Box>
        );
      case 8:
        return (
          <Box key={question.id} id={`question-${question.id}`}>
            <PullDownQuestion
              question={questionWithAnswer}
              themeColor={themeColor}
              currentQuestion={questionNumber}
              totalQuestions={totalQuestions}
              onAnswerChange={handleAnswerChange}
            />
          </Box>
        );
      default:
        return (
          <Box key={question.id} sx={{ py: 4 }}>
            <Typography>Unsupported question type: {question.question_types_id}</Typography>
          </Box>
        );
    }
  };

  if (loading) {
    return null;
  }

  const themeColor = formSettings?.theme_color || '#8C52FF';
  const headerImage = questionSettings?.header_image_url || 
    'https://misezukuri.com/wp-content/uploads/2023/10/Cafebar1.png';
  const logoUrl = formSettings?.logo_image_url;

  return (
    <>
      <PreviewIndicator isPreviewMode={isPreviewMode} themeColor={themeColor} />
      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes highlightError {
              0% {
                background-color: transparent;
              }
              50% {
                background-color: rgba(244, 67, 54, 0.1);
                box-shadow: 0 0 20px rgba(244, 67, 54, 0.3);
              }
              100% {
                background-color: transparent;
              }
            }
          `
        }}
      />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: isMobile ? '#F1F4F8' : '#FFFFFF',
          // Hide scrollbars
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          // Prevent mobile overscroll bounce
          overscrollBehavior: 'none',
          // Prevent pull-to-refresh on mobile
          touchAction: 'pan-x pan-y'
        }}
      >
        {/* Glass Header Bar for PC */}
        {!isMobile && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 65,
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
              zIndex: 10,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.02) 100%)',
                pointerEvents: 'none'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                pointerEvents: 'none'
              }
            }}
          >
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  width: '140px',
                  height: '55px',
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 1,
                  filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))'
                }}
              />
            )}
          </Box>
        )}

        {/* Header Image Section */}
        <Box
          sx={{
            position: 'relative',
            height: isMobile ? 250 : 270,
            overflow: 'hidden',
            width: '100%',
            marginTop: isMobile ? 0 : '65px'
          }}
        >
          {/* Background Image */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${headerImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />

          {/* Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isMobile
                ? 'linear-gradient(to bottom, transparent 0%, rgba(255, 255, 255, 0.8) 70%, rgba(255, 255, 255, 1) 100%)'
                : 'linear-gradient(to bottom, transparent 0%, rgba(255, 255, 255, 0.6) 85%, rgba(255, 255, 255, 1) 100%)'
            }}
          />

          {/* Logo for Mobile only */}
          {isMobile && logoUrl && (
            <Box
              sx={{
                position: 'absolute',
                top: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 120,
                height: 50,
                zIndex: 2
              }}
            >
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </Box>

        {/* Content Container - Limited Width */}
        <Container
          maxWidth={false}
          sx={{
            width: isMobile ? '100%' : '100%',
            // Hide scrollbars
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            maxWidth: isMobile ? '100%' : '900px',
            margin: '0 auto',
            backgroundColor: '#FFFFFF',
            minHeight: isMobile ? 'calc(100vh - 250px)' : 'calc(100vh - 350px)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: isMobile ? 'calc(100vh - 250px)' : 'calc(100vh - 350px)',
              px: isMobile ? 3 : 4,
              py: isMobile ? 0 : 2
            }}
          >
            {/* Questions Content */}
            <Box sx={{ flex: 1 }}>
              {questions.map((question, index) => (
                <Box key={question.id} sx={{ mb: '50px' }}>
                  {renderQuestion(question, index)}
                </Box>
              ))}


              {/* Navigation Section */}
              <Box sx={{ 
                py: 4,
                // Chrome mobile navigation protection
                pb: isMobile ? 'max(48px, env(safe-area-inset-bottom))' : 4
              }}>
                {/* Main Button */}
                <Box sx={{ 
                  textAlign: 'center',
                  // Additional protection for the button itself
                  mb: isMobile ? 'max(16px, env(safe-area-inset-bottom))' : 0
                }}>
                  <Button
                    variant="contained"
                    onClick={handleNextClick}
                    sx={{
                      backgroundColor: stringToColor(themeColor),
                      color: 'white',
                      width: 200,
                      height: 50,
                      borderRadius: '24px',
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontFamily: '"Noto Sans JP", sans-serif',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: stringToColor(themeColor),
                        opacity: 0.9,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                      }
                    }}
                  >
                    {currentPage >= allPages.length && !hasRegularQuestions ? '回答を送信' : '次へ進む'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Submit Confirmation Dialog */}
      <Dialog
        open={showSubmitDialog}
        onClose={() => !submitting && setShowSubmitDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            padding: isMobile ? 2 : 3,
            margin: isMobile ? 2 : 3,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(8px)'
          }
        }}
      >
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {/* Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: `${stringToColor(themeColor)}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              border: `2px solid ${stringToColor(themeColor)}30`
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 2,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 3,
                  height: 16,
                  backgroundColor: stringToColor(themeColor),
                  borderRadius: '1.5px'
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 2,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 3,
                  height: 3,
                  backgroundColor: stringToColor(themeColor),
                  borderRadius: '50%'
                }
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: '#1a1a1a',
              fontSize: isMobile ? '1.3rem' : '1.5rem',
              letterSpacing: '-0.02em'
            }}
          >
            回答を送信しますか？
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              color: '#6b7280',
              mb: 4,
              fontSize: isMobile ? '0.9rem' : '1rem',
              lineHeight: 1.6,
              fontWeight: 400
            }}
          >
            送信後は回答を変更できません。<br />
            内容をご確認の上、送信してください。
          </Typography>

          {/* Actions */}
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 2,
              flexDirection: isMobile ? 'column-reverse' : 'row',
              justifyContent: 'center'
            }}
          >
            <Button
              onClick={() => setShowSubmitDialog(false)}
              disabled={submitting}
              sx={{
                minWidth: isMobile ? '100%' : 120,
                height: 48,
                borderRadius: '12px',
                color: '#6b7280',
                backgroundColor: 'rgba(107, 114, 128, 0.08)',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.95rem',
                border: '1px solid rgba(107, 114, 128, 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(107, 114, 128, 0.12)',
                  borderColor: 'rgba(107, 114, 128, 0.25)'
                }
              }}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleSubmitConfirm}
              disabled={submitting}
              variant="contained"
              sx={{
                minWidth: isMobile ? '100%' : 120,
                height: 48,
                borderRadius: '12px',
                backgroundColor: stringToColor(themeColor),
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.95rem',
                boxShadow: `0 4px 20px ${stringToColor(themeColor)}40`,
                '&:hover': {
                  backgroundColor: stringToColor(themeColor),
                  boxShadow: `0 6px 25px ${stringToColor(themeColor)}50`,
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  backgroundColor: 'rgba(107, 114, 128, 0.3)',
                  color: 'rgba(107, 114, 128, 0.6)',
                  boxShadow: 'none'
                }
              }}
            >
              {submitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress 
                    size={18} 
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }} 
                  />
                  <span>送信中...</span>
                </Box>
              ) : (
                '送信する'
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Drawing Loading Screen */}
      {showDrawingLoading && (
        <DrawingLoadingScreen
          onComplete={handleDrawingComplete}
          themeColor={stringToColor(formSettings?.theme_color)}
        />
      )}

    </>
  );
};

export default RequiredQuestionsPage;